const SELECTORS = {
  root: '[data-authenticated-layout-root]',
  popupImageTrigger: '.show-popup-images',
  drawer: '[data-lapri-drawer]',
  drawerBackdrop: '[data-lapri-drawer-backdrop]',
  drawerOpen: '[data-lapri-drawer-open]',
  drawerClose: '[data-lapri-drawer-close]',
  drawerBody: '[data-lapri-drawer-body]',
  drawerCategoryButton: '[data-drawer-category-button]',
  drawerCategoryPanel: '[data-drawer-category-panel]',
  dropdown: '[data-lapri-dropdown]',
  dropdownToggle: '[data-lapri-dropdown-toggle]',
  dropdownMenu: '[data-lapri-dropdown-menu]',
  debugTarget: '[data-debug-icon]'
};

const queryAll = (selector, root = document) => Array.from(root.querySelectorAll(selector));
const query = (selector, root = document) => root.querySelector(selector);
const getJQuery = () => window.jQuery || window.$;

const showToast = (message, className) => {
  const $ = getJQuery();
  if (!$ || !message || typeof $.fn?.toast !== 'function') return;

  $(document.body).toast({ message, showProgress: 'bottom', class: className });
};

const showModal = (selector) => {
  const $ = getJQuery();
  if (!$ || typeof $.fn?.modal !== 'function') return;

  const modal = $(selector);
  if (modal.length > 0) {
    modal.modal('show');
  }
};

const createFlashController = (element) => ({
  connect() {
    showToast(element.dataset.flashNotice, 'teal');
    showToast(element.dataset.flashAlert, 'red');
  }
});

const createPopupImageController = (trigger) => ({
  connect() {
    trigger.addEventListener('click', () => {
      const modalId = trigger.dataset.images;
      if (!modalId) return;

      showModal(`#${modalId}`);
    });
  }
});

const createDrawerController = (root) => {
  const drawer = query(SELECTORS.drawer, root);
  const drawerBackdrop = query(SELECTORS.drawerBackdrop, root);
  const drawerOpen = query(SELECTORS.drawerOpen, root);
  const drawerClose = query(SELECTORS.drawerClose, root);
  const drawerBody = query(SELECTORS.drawerBody, root);
  const categoryButtons = queryAll(SELECTORS.drawerCategoryButton, root);
  const categoryPanels = queryAll(SELECTORS.drawerCategoryPanel, root);
  const supportsPointerEvents = typeof window.PointerEvent === 'function';

  if (!drawer || !drawerBackdrop || !drawerOpen || !drawerClose || !drawerBody) return null;

  const currentCategory =
    drawer.dataset.activeCategory ||
    categoryButtons[0]?.dataset.drawerCategoryButton;

  const isMousePointerEvent = (event) => event.pointerType === 'mouse';

  const activateCategory = (categoryId) => {
    categoryButtons.forEach((button) => {
      button.classList.toggle('is-active', button.dataset.drawerCategoryButton === categoryId);
    });

    categoryPanels.forEach((panel) => {
      panel.classList.toggle('is-active', panel.dataset.drawerCategoryPanel === categoryId);
    });
  };

  const scrollActiveCategoryIntoView = () => {
    const activeButton = categoryButtons.find((button) =>
      button.classList.contains('is-active')
    );

    if (!activeButton) return;

    activeButton.scrollIntoView({
      behavior: 'auto',
      block: 'nearest',
      inline: 'nearest'
    });
  };

  const setDrawerOpen = (open) => {
    drawer.classList.toggle('is-open', open);
    drawerBackdrop.hidden = !open;
    drawerOpen.setAttribute('aria-expanded', String(open));

    if (!open || !currentCategory) return;

    activateCategory(currentCategory);

    window.requestAnimationFrame(() => {
      scrollActiveCategoryIntoView();
    });
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawer.classList.contains('is-open'));
  };

  return {
    connect() {
      drawerOpen.addEventListener('click', toggleDrawer);

      drawerClose.addEventListener('click', () => {
        setDrawerOpen(false);
      });

      drawerBackdrop.addEventListener('click', () => {
        setDrawerOpen(false);
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          setDrawerOpen(false);
        }
      });

      categoryButtons.forEach((button) => {
        ['focus', 'click'].forEach((eventName) => {
          button.addEventListener(eventName, () => {
            activateCategory(button.dataset.drawerCategoryButton);
          });
        });

        if (supportsPointerEvents) {
          button.addEventListener('pointerenter', (event) => {
            if (!isMousePointerEvent(event)) return;

            activateCategory(button.dataset.drawerCategoryButton);
          });
        } else {
          button.addEventListener('mouseenter', () => {
            activateCategory(button.dataset.drawerCategoryButton);
          });
        }
      });
    }
  };
};

const createDropdownController = (dropdown, closeAllDropdowns) => {
  const toggle = query(SELECTORS.dropdownToggle, dropdown);
  const menu = query(SELECTORS.dropdownMenu, dropdown);

  if (!toggle || !menu) return null;

  const closeMenu = () => {
    menu.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
  };

  return {
    connect() {
      toggle.addEventListener('click', (event) => {
        event.stopPropagation();
        const willOpen = menu.hidden;
        closeAllDropdowns();
        menu.hidden = !willOpen;
        toggle.setAttribute('aria-expanded', String(willOpen));
      });

      dropdown.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          closeMenu();
        }
      });
    }
  };
};

const createDebugController = (targets) => ({
  connect() {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('debug')) return;

    if (targets.length === 0) {
      alert('この画面でデバッグできるものはありません');
      return;
    }

    targets.forEach((target) => {
      target.style.display = '';

      if (target.hasAttribute('data-debug-month')) return;

      target.addEventListener('click', () => {
        console.log(target.dataset.debugStr);
      });

      target.addEventListener('dblclick', () => {
        alert(target.dataset.debugStr);
      });
    });

    const targetTitles = [...new Set(targets.map((target) => target.dataset.debugTitle).filter(Boolean))].join(', ');
    alert(`${targetTitles}のデバッグができます。\n「i」マークのダブルクリックでアラート、\nシングルクリックでconsoleに出力します`);
  }
});

const initializeAuthenticatedLayout = () => {
  const root = query(SELECTORS.root) || document;
  const controllers = [];

  controllers.push(createFlashController(document.body));

  queryAll(SELECTORS.popupImageTrigger, root).forEach((trigger) => {
    controllers.push(createPopupImageController(trigger));
  });

  const drawerController = createDrawerController(root);
  if (drawerController) {
    controllers.push(drawerController);
  }

  const closeAllDropdowns = () => {
    queryAll(SELECTORS.dropdownMenu, root).forEach((menu) => {
      menu.hidden = true;
    });

    queryAll(SELECTORS.dropdownToggle, root).forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'false');
    });
  };

  queryAll(SELECTORS.dropdown, root).forEach((dropdown) => {
    const dropdownController = createDropdownController(dropdown, closeAllDropdowns);
    if (dropdownController) {
      controllers.push(dropdownController);
    }
  });

  document.addEventListener('click', closeAllDropdowns);

  controllers.push(createDebugController(queryAll(SELECTORS.debugTarget)));

  controllers.forEach((controller) => controller.connect());
};

document.addEventListener('DOMContentLoaded', initializeAuthenticatedLayout);
